import { useEffect, useRef, useState, useCallback } from 'react';
import { Loader2, Check, AlertTriangle } from 'lucide-react';
import { api } from '../../utils/api';
import { useTranslation } from '../../i18n';

export const TRIAL_LEVELS = ['Starter', 'A1', 'A2', 'B1', 'B2', 'C1'];

const TEXTAREA_FIELDS = ['experience', 'interests', 'strengths', 'weaknesses', 'personality', 'goals', 'notes'];

const AUTOSAVE_DELAY_MS = 700;

export const trialFormHasContent = (data) =>
  Object.values(data || {}).some(v => String(v ?? '').trim() !== '');

const calcAge = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 ? age : null;
};

const inputCls = 'w-full rounded-lg border border-brand-light bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30';
const labelCls = 'block text-xs font-medium text-brand-primary/80 mb-1';

// Teacher-only trial lesson form shown as the third sidebar tab in the
// classroom. Autosaves to /api/trial-forms/lesson/:id; the server finalizes
// and sends it to admins when the lesson is completed.
const TrialFormPanel = ({ lessonId, onContentChange }) => {
  const { t } = useTranslation();
  const tf = (k, v) => t(`trialForm.${k}`, v);

  const [data, setData] = useState({});
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [submitted, setSubmitted] = useState(false);

  const pendingRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: res, error } = await api.get(`/api/trial-forms/lesson/${lessonId}`);
      if (cancelled) return;
      if (error) {
        setLoadError(true);
        setLoading(false);
        return;
      }
      const initial = { ...(res.form?.data || {}) };
      if (!initial.age) {
        const age = calcAge(res.student?.date_of_birth);
        if (age !== null) initial.age = String(age);
      }
      setData(initial);
      setPrograms(res.programs || []);
      setSubmitted(!!res.form?.submitted_at);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [lessonId]);

  useEffect(() => {
    onContentChange?.(trialFormHasContent(data));
  }, [data, onContentChange]);

  const flush = useCallback(async () => {
    clearTimeout(timerRef.current);
    const payload = pendingRef.current;
    if (!payload) return;
    pendingRef.current = null;
    setSaveState('saving');
    const { error } = await api.put(`/api/trial-forms/lesson/${lessonId}`, { data: payload });
    // A newer edit may have queued while this request was in flight.
    if (!pendingRef.current) setSaveState(error ? 'error' : 'saved');
  }, [lessonId]);

  // Flush unsaved edits when the panel unmounts (lesson ended / page left).
  useEffect(() => () => { flush(); }, [flush]);

  const update = (key, value) => {
    setData(prev => {
      const next = { ...prev, [key]: value };
      pendingRef.current = next;
      return next;
    });
    setSaveState('saving');
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, AUTOSAVE_DELAY_MS);
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-brand-primary/60">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return <div className="flex-1 p-4 text-sm text-red-600">{tf('loadError')}</div>;
  }

  const textarea = (key) => (
    <div key={key}>
      <label className={labelCls}>{tf(`fields.${key}`)}</label>
      <textarea
        rows={2}
        value={data[key] || ''}
        onChange={e => update(key, e.target.value)}
        placeholder={tf(`placeholders.${key}`)}
        className={`${inputCls} resize-y min-h-[2.5rem]`}
      />
    </div>
  );

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs text-brand-primary/60 leading-snug">{tf('intro')}</p>
        <span className="flex-shrink-0 flex items-center gap-1 text-xs text-brand-primary/60 min-h-[1rem]">
          {saveState === 'saving' && (<><Loader2 size={12} className="animate-spin" />{tf('saving')}</>)}
          {saveState === 'saved' && (<><Check size={12} />{tf('saved')}</>)}
          {saveState === 'error' && (<span className="flex items-center gap-1 text-red-600"><AlertTriangle size={12} />{tf('saveError')}</span>)}
        </span>
      </div>
      {submitted && (
        <div className="text-xs rounded-lg bg-brand-light/40 text-brand-primary px-3 py-1.5">{tf('submitted')}</div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>{tf('fields.level')}</label>
          <select value={data.level || ''} onChange={e => update('level', e.target.value)} className={inputCls}>
            <option value="">{tf('levelNone')}</option>
            {TRIAL_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>{tf('fields.age')}</label>
          <input
            value={data.age || ''}
            onChange={e => update('age', e.target.value)}
            placeholder={tf('placeholders.age')}
            className={inputCls}
          />
        </div>
      </div>

      {TEXTAREA_FIELDS.filter(k => k !== 'notes').map(textarea)}

      <div>
        <label className={labelCls}>{tf('fields.recommended_program_id')}</label>
        <select
          value={data.recommended_program_id || ''}
          onChange={e => update('recommended_program_id', e.target.value)}
          className={inputCls}
        >
          <option value="">{tf('programNone')}</option>
          {programs.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
      </div>

      <div>
        <label className={labelCls}>{tf('fields.recommended_frequency')}</label>
        <div className="flex gap-1.5">
          {['1', '2', '3'].map(f => (
            <button
              key={f}
              type="button"
              onClick={() => update('recommended_frequency', data.recommended_frequency === f ? '' : f)}
              className={`flex-1 rounded-lg border px-2 py-1.5 text-xs transition ${
                data.recommended_frequency === f
                  ? 'bg-[#2A4D25] border-[#2A4D25] text-white'
                  : 'bg-white border-brand-light text-brand-primary hover:bg-brand-light/30'
              }`}
            >
              {tf(`frequency.${f}`)}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className={labelCls}>{tf('fields.student_liked')}</label>
        <div className="flex gap-1.5">
          {['yes', 'maybe', 'no'].map(v => (
            <button
              key={v}
              type="button"
              onClick={() => update('student_liked', data.student_liked === v ? '' : v)}
              className={`flex-1 rounded-lg border px-2 py-1.5 text-xs transition ${
                data.student_liked === v
                  ? 'bg-[#2A4D25] border-[#2A4D25] text-white'
                  : 'bg-white border-brand-light text-brand-primary hover:bg-brand-light/30'
              }`}
            >
              {tf(`liked.${v}`)}
            </button>
          ))}
        </div>
      </div>

      {textarea('notes')}
    </div>
  );
};

export default TrialFormPanel;
